import { useEffect, useState } from "react";
import axios from "axios";
import { Inbox, Download, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuoteList = ({ token }) => {
  const [quotes, setQuotes] = useState(null);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filtered = (quotes || []).filter(
    (x) =>
      !q ||
      (x.institution_name || "").toLowerCase().includes(q) ||
      (x.contact_person || "").toLowerCase().includes(q) ||
      (x.reference || "").toLowerCase().includes(q) ||
      (x.phone || "").includes(q)
  );

  useEffect(() => {
    axios
      .get(`${API}/admin/quotes`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setQuotes(res.data.quotes))
      .catch(() => setQuotes([]));
  }, [token]);

  if (quotes === null) {
    return <div className="grid gap-4">{[1, 2, 3].map((i) => <div key={i} className="h-32 bg-white border border-[#221533]/10 rounded-xl animate-pulse" />)}</div>;
  }

  if (!quotes.length) {
    return (
      <div data-testid="quotes-empty" className="text-center py-20 text-[#221533]/45">
        <Inbox size={36} className="mx-auto mb-4 text-[#8E5A9E]/50" />
        <p className="font-light">No quote requests yet — they will appear here as institutions order.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="relative mb-5 max-w-md">
        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#221533]/40" />
        <input
          data-testid="admin-quote-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search quotes — institution, name, reference, phone…"
          className="w-full bg-white border border-[#221533]/15 rounded-full pl-11 pr-4 py-3 text-sm text-[#221533] placeholder-[#221533]/40 focus:border-[#8E5A9E] transition-colors"
        />
      </div>
      <div data-testid="quotes-list" className="grid gap-4">
      {filtered.map((q) => (
        <div key={q.reference} data-testid={`quote-${q.reference}`} className="bg-white border border-[#221533]/10 rounded-xl p-5 sm:p-6 hover:border-[#8E5A9E]/40 transition-colors">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <p className="font-clash text-xl font-medium text-[#221533]">{q.institution_name}</p>
              <p className="text-sm text-[#221533]/55 mt-1">
                {q.contact_person} · {q.phone} · {q.email}
              </p>
            </div>
            <div className="text-right">
              <span className="font-mono2 text-[11px] tracking-[0.15em] bg-[#8E5A9E]/10 text-[#74427F] px-3 py-1.5 rounded-full">{q.reference}</span>
              <p className="font-mono2 text-[10px] text-[#221533]/40 mt-2">
                {q.institution_type} · {new Date(q.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </p>
              <a
                href={`${API}/quotes/${q.reference}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`quote-pdf-${q.reference}`}
                className="inline-flex items-center gap-1.5 mt-2.5 text-[11px] font-mono2 uppercase tracking-[0.15em] text-[#74427F] hover:text-white hover:bg-[#8E5A9E] border border-[#8E5A9E]/30 px-3 py-1.5 rounded-md transition-colors"
              >
                <Download size={12} /> PDF
              </a>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {q.items.map((i) => (
              <span key={i.slug} className="text-xs bg-[#FAF8FC] border border-[#221533]/10 px-3 py-1.5 rounded-full text-[#221533]/70">
                {i.name}{i.pack_size ? ` (${i.pack_size})` : ""} × <b>{i.qty}</b>{i.price ? <span className="text-[#74427F] font-semibold"> · {i.price}</span> : null}
              </span>
            ))}
          </div>
          {q.message && <p className="mt-4 text-sm text-[#221533]/60 font-light border-t border-[#221533]/10 pt-3">“{q.message}”</p>}
        </div>
      ))}
      </div>
    </div>
  );
};
